/**
 * What the publish check says, in the console's words: the backend's messages name ids, rails,
 * terms and "published"; these name the items, rows, categories and "in the app". Messages this
 * does not know pass through with a capital letter. Pure, unit-tested.
 */

import type { Pack } from "../vocabulary/vocabulary";

type Name = (id: string) => string | undefined;

const ACCESS_WORDS: Record<string, string> = { subscription: "members", purchase: "buyers", rental: "renters" };

export function plainProblem(message: string, pack: Pack, name: Name): string {
  const q = (id: string) => `“${name(id) ?? id}”`;
  const category = pack.nouns.taxonomy.one.toLowerCase();
  const collection = pack.nouns.collection.one.toLowerCase();
  const include = "Show it in the app to include it.";
  const rules: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
    [/^leaves out rail for term (\S+), which is not published$/, (m) => `A row of the ${category} ${q(m[1]!)} is left out: that ${category} is not in the app. ${include}`],
    [/^leaves out rail for (\S+), which is not published$/, (m) => `The row ${q(m[1]!)} is left out: that ${collection} is not in the app. ${include}`],
    [/^season (\S+) leaves out (\S+), which is not published$/, (m) => `Season ${q(m[1]!)} leaves out ${q(m[2]!)}: it is not in the app. ${include}`],
    [/^leaves out (\S+), which is not published$/, (m) => `${q(m[1]!)} is left out: it is not in the app. ${include}`],
    [/^credit for (\S+) left out: not published$/, (m) => `${q(m[1]!)} is left out of the credits: not in the app. ${include}`],
    [/^term (\S+) left out: not published$/, (m) => `The ${category} ${q(m[1]!)} is left out: not in the app. ${include}`],
    [/^stage (\S+) left out: not published$/, (m) => `The stage ${q(m[1]!)} is left out: not in the app. ${include}`],
    [/^"(.*)" is a placeholder$/, (m) => `“${m[1]}” is placeholder text. Replace it with the real thing.`],
    [/^(subscription|purchase|rental) access needs at least one product id$/, (m) => `It is for ${ACCESS_WORDS[m[1]!]}, but nothing unlocks it. Choose what does under Who can watch.`],
    [/^a page with id "home" is required$/, () => "Your app has no Home screen yet. Add one under Your app."],
    [/^credits unknown person (\S+)$/, (m) => `A credit names someone who is not in your Library (${q(m[1]!)}).`],
    [/^uses unknown term (\S+)$/, (m) => `It uses a ${category} that does not exist (${q(m[1]!)}).`],
  ];
  for (const [pattern, words] of rules) {
    const m = message.match(pattern);
    if (m) return words(m);
  }
  return message ? message[0]!.toUpperCase() + message.slice(1) : message;
}

/**
 * The setup guide's step that clears a problem, when there is one: on a brand still being set up,
 * such a problem is the guide's next job, not an alarm (Home shows it on that step).
 */
export type SetupStep = "brand" | "item" | "artwork" | "screen" | "publish";

export function setupStepOf(message: string): SetupStep | undefined {
  if (/^a page with id "home" is required$/.test(message)) return "screen";
  return undefined;
}
